<script setup>
const ui = useUi()
const user = useUser()
const config = useRuntimeConfig()

const crowdfundingUrl = computed(() => config.public.CROWDFUNDING_URL)

defineProps({
  showMobileNav: {
    type: Boolean,
    default: true
  }
})
const emit = defineEmits(['toggle-navbar'])

const freeWeekend = useFreeWeekend()
const mostRecentFw = await useFreeWeekend().getRecentFw()
freeWeekend.data = mostRecentFw || {
  is_enabled: false,
  is_active: false
}

const route = useRoute()
const { getFlashMessageKey } = useHelpers()

const logoLink = route.path === '/' ? '#top' : '/#top'

const sitemap = computed(() => {
  const hasTeamAccess = user.data?.has_team_access && config.public.TEAMS_DASHBOARD_ENABLED === 'true'
  const isTeamsFeatureDisabled = config.public.TEAMS_DASHBOARD_ENABLED !== 'true'

  return [
    {
      route: 'Available Certificates',
      url: '/#available-certificates'
    },
    ...(mostRecentFw?.tech
      ? [
          {
            route: 'Free Weekend',
            url: `/${mostRecentFw.tech}/free-weekend`
          }
        ]
      : []),
    ...(hasTeamAccess
      ? [
          {
            route: 'Team Dashboard',
            url: '/team/dashboard'
          }
        ]
      : isTeamsFeatureDisabled
        ? [
            {
              route: 'Cast your Vote',
              url: '/#cast-your-vote'
            }
          ]
        : []),
    {
      route: 'Hire Developers',
      url: '/#hire-developers'
    },
    {
      route: 'Blog',
      url: '/blog',
      target: ''
    },
    ...(crowdfundingUrl.value ? [{ route: 'Crowdfunding', url: crowdfundingUrl.value, target: '_blank', enabled: true }] : [])
  ]
})

const isNavOpen = ref(false)

function openAuthModal (mode) {
  ui.flashMessageKey = getFlashMessageKey()
  ui.openAuthModal({
    mode
  })
}

function toggle () {
  isNavOpen.value = !isNavOpen.value
  emit('toggle-navbar', isNavOpen.value)
}

async function logout () {
  await user.logout()
  window.location.href = '/'
}
</script>

<template>
  <div class="sticky top-0 z-[60] w-full bg-white/60 backdrop-blur-2xl dark:bg-eerie-black/60">
    <BannerTop />
    <BannerTopHome />
    <BannerTopJavascript />
    <BannerTopNuxt />
    <BannerTopReact />
    <BannerTopAngular />
    <AppContainer class="flex h-[74px] items-center">
      <Teleport
        v-if="showMobileNav"
        to="body">
        <div
          v-if="isNavOpen"
          class=" fixed left-0 top-15 z-[1000] h-fit w-72 overflow-y-scroll bg-white shadow-xl xl:hidden dark:bg-dark-gunmetal">
          <nav class="relative w-full p-6">
            <ul class="flex flex-col gap-6">
              <li
                v-for="item in sitemap"
                :key="item.link"
                class="font-bold text-eerie-black hover:text-azure-radiance dark:text-body-text dark:hover:text-azure-radiance">
                <FreeWeekendButton
                  v-if="item.url?.includes('free-weekend')"
                  v-bind="{ link: item.url, label: item.route, target: item.target }"
                  @click="isNavOpen=false" />

                <NuxtLink
                  v-else
                  :to="item.url"
                  :target="item.target"
                  class="flex items-center whitespace-nowrap"
                  @click="isNavOpen=false">
                  {{ item.route }}
                  <InlineSvg
                    v-if="item.icon"
                    hard-code
                    :src="item.icon"
                    class="h-4 fill-current" />
                </NuxtLink>
              </li>
              <AppThemeSwitch class="w-fit" />
            </ul>
          </nav>
        </div>
      </Teleport>
      <div class="flex w-full items-center justify-between gap-4 md:gap-8">
        <div class="flex items-center gap-2 md:gap-8">
          <AppToggleNav
            class="xl:hidden"
            v-bind="{ isNavOpen }"
            @click="toggle" />
          <NuxtLink :to="logoLink">
            <InlineSvg
              hard-code
              src="/images/homepage/logo-certificates-footer.svg"
              class="hidden h-6 w-auto md:h-10 dark:block" />
            <InlineSvg
              hard-code
              src="/images/homepage/logo-certificates-light.svg"
              class="h-6 w-auto md:h-10 dark:hidden" />
          </NuxtLink>
        </div>
        <div class=" flex w-fit items-center gap-4 xl:gap-8">
          <ul class="hidden items-center gap-6 xl:inline-flex xl:gap-8">
            <li
              v-for="item in sitemap"
              :key="item.url"
              class="font-bold text-eerie-black hover:text-azure-radiance dark:text-body-text dark:hover:text-azure-radiance">
              <FreeWeekendButton
                v-if="item.url?.includes('free-weekend')"
                v-bind="{ link: item.url, label: item.route, target: item.target }"
                @click="isNavOpen=false">
                <InlineSvg
                  v-if="item.icon"
                  hard-code
                  :src="item.icon"
                  class="h-4 fill-current" />
              </FreeWeekendButton>

              <NuxtLink
                v-else
                :to="item.url"
                :target="item.target"
                class="flex items-center whitespace-nowrap">
                {{ item.route }}
                <InlineSvg
                  v-if="item.icon"
                  hard-code
                  :src="item.icon"
                  class="h-4 fill-current" />
              </NuxtLink>
            </li>
          </ul>
          <template v-if="user.isGuest">
            <button
              class="whitespace-nowrap text-sm font-bold text-eerie-black hover:text-azure-radiance md:text-base dark:text-white dark:hover:text-azure-radiance"
              @click="openAuthModal('login')">
              Login
            </button>
            <button
              class="btn-primary-neutral whitespace-nowrap text-sm md:text-base"
              @click="openAuthModal()">
              Sign up
            </button>
          </template>
          <div
            v-else
            class="flex items-center gap-7.5">
            <button
              class="btn-primary-outline block px-6 py-3 font-bold text-eerie-black hover:text-azure-radiance xl:!hidden dark:text-white dark:hover:text-azure-radiance"
              @click="logout">
              Log Out
            </button>

            <HomeNavigationDropdown class="hidden xl:block" />
          </div>
          <AppThemeSwitch class="hidden xl:flex" />
        </div>
      </div>
    </AppContainer>
  </div>
  <AuthModal />
</template>
